import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import useCopagoMatching from '../../hooks/useCopagoMatching';

const formatCents = cents =>
  Number.isFinite(cents) ? `${(cents / 100).toFixed(2)} €` : '—';

const formatDuration = durationMS =>
  Number.isFinite(durationMS) ? `${Math.round(durationMS / 60000)} Min` : '—';

const describeItem = item => {
  if (item.type === 'upgrade') {
    return `Upgrade „${item.name}“`;
  }
  const tier = Number.isFinite(item.minPersons) ? ` · ab ${item.minPersons} Pers.` : '';
  return `${item.productName} · ${item.pricingList} · ${item.category} (${formatDuration(
    item.durationMS
  )})${tier}`;
};

// Shows the stored price discrepancy report (recomputed nightly by cron) and
// lets the user trigger a fresh check on demand.
export default function CopagoPriceDiscrepancyDialog({ open, onClose }) {
  const { isLoading, getPriceDiscrepancies } = useCopagoMatching();
  const [report, setReport] = useState(null);

  const load = useCallback(
    async ({ recompute = false } = {}) => {
      try {
        const data = await getPriceDiscrepancies({ recompute });
        if (data) setReport(data);
      } catch (err) {
        // toast already shown by the hook
      }
    },
    [getPriceDiscrepancies]
  );

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = useCallback(() => {
    setReport(null);
    onClose();
  }, [onClose]);

  const items = report?.items || [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle>Copago-Preisdiskrepanzen</DialogTitle>
      <DialogContent dividers>
        {isLoading && !report ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Typography variant='body2' color='text.secondary'>
              Vergleicht die Preise der verkauften Leistungen (Upgrades, Preismatrix-Einträge) mit
              dem Preis des gematchten Copago-Artikels. Die Prüfung läuft automatisch jede Nacht.
              {report?.computedAt
                ? ` Letzte Prüfung: ${dayjs(report.computedAt).format('DD.MM.YYYY HH:mm')}.`
                : ' Es liegt noch kein Prüfergebnis vor.'}
            </Typography>

            {items.length === 0 ? (
              <Alert severity='success'>Keine Preisdiskrepanzen gefunden.</Alert>
            ) : (
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Verkaufte Leistung</TableCell>
                    <TableCell>Verkaufspreis</TableCell>
                    <TableCell>Copago-Artikel</TableCell>
                    <TableCell>Copago-Preis</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={`${item.type}-${item.entryId || item.upgradeId}-${index}`}>
                      <TableCell>
                        {describeItem(item)}
                        {item.matchSource === 'pricing' ? (
                          <Typography variant='caption' color='text.secondary' display='block'>
                            Artikel von Preisliste geerbt
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{formatCents(item.soldPriceCents)}</TableCell>
                      <TableCell>
                        {item.article ? `${item.article.copagoId} – ${item.article.name}` : '—'}
                      </TableCell>
                      <TableCell>{formatCents(item.article?.priceCents)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => load({ recompute: true })}
          disabled={isLoading}
        >
          Jetzt neu prüfen
        </Button>
        <Button onClick={handleClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}
